import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { Asset } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { slackHook } from "src/utils/snug-slack-hooks";
import { useMounted } from "../use-mounted";

// Action Types
type AssetsStoreAction =
  | { type: "SET_ASSETS"; payload: Asset[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_ASSETS"; payload: Asset }
  | { type: "DELETE_ASSETS"; payload: string }
  | { type: "SET_ASSETS_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_ASSETS_SAVING_NEW"; payload: boolean };

// Reducer Function
const assetsStoreReducer = (
  state: AssetsStoreState,
  action: AssetsStoreAction
): AssetsStoreState => {
  switch (action.type) {
    case "SET_ASSETS":
      return {
        ...state,
        assets: action.payload,
        assetsLoading: false,
      };

    case "SET_LOADING":
      return { ...state, assetsLoading: action.payload };

    case "ADD_OR_UPDATE_ASSETS":
      const index = state.assets.findIndex((l) => l.id === action.payload.id);
      let updatedAssets = [...state.assets];
      if (index > -1) {
        updatedAssets[index] = action.payload;
      } else {
        updatedAssets.push(action.payload);
      }
      return { ...state, assets: updatedAssets };

    case "DELETE_ASSETS":
      return {
        ...state,
        assets: state.assets.filter((l) => l.id !== action.payload),
      };
    case "SET_ASSETS_FILES_TO_UPLOAD":
      return { ...state, assetsFilestoUpload: action.payload };
    case "SET_ASSETS_SAVING_NEW":
      return { ...state, assetsSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface AssetsStoreState {
  assets: Asset[];
  assetsLoading: boolean;
  assetsSavingNew: boolean;
  assetsFilestoUpload: File[];
}

// Hook
export const useAssetsStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(assetsStoreReducer, {
    assets: [],
    assetsLoading: true,
    assetsFilestoUpload: [],
    assetsSavingNew: false,
  });

  const deleteAssetsById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchAssetsData = useV3GenericGetData<Asset[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createAssets = useV3Create<Asset>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateAssets = useV3Update<Asset>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<Asset>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleAssetsFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.assetsFilestoUpload;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "assets",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_ASSETS",
              payload: res,
            });

            const updatedFiles = state.assetsFilestoUpload.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_ASSETS_FILES_TO_UPLOAD",
              payload: updatedFiles,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            slackHook(
              `Document Error: Failed to upload document for entity_id: ${entityId} and document_id: ${currentFile.path}`
            );
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          slackHook(
            `Document Error: Failed to upload document for entity_id: ${entityId} and document_id: ${currentFile.path} with message "${error}"`
          );
        }
      }
    },
    [state.assetsFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadAssetsDocument = useCallback(
    async (assets_id: string, document_id: string | null) => {
      if (!document_id || !assets_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading file...");

      const params: any = {
        document_id: document_id,
        entity: "assets",
        entity_id: assets_id,
        ud_id: ud_id,
      };

      downloadDocument(params)
        .then((res: any) => {
          if (res) {
            // download File from s3 res and get file name from mathcing document id in assets objects' documents array
            downloadFileFromS3(
              res,
              state.assets
                ?.find((p) => p.id === assets_id)
                ?.documents?.find((d) => d.document_id === document_id)
                ?.document_name || "Snug_Download.error"
            );
          } else {
            slackHook(
              `Document Error: Failed to get document with message "No response"`
            );
          }
        })
        .catch((err) => {
          toast.error("Failed to download file");
          slackHook(
            `Document Error: Failed to get document with message "${err}"`
          );
        });
    },
    [downloadDocument, state.assets, ud_id]
  );

  const handleDeleteAssetsDocument = useCallback(
    async (assets_id: string | null, document_id: string | null): Promise<void> => {
      if (!document_id || !assets_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      try {
        dispatch({ type: "SET_ASSETS_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "assets",
          entity_id: assets_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the assets object
          const updatedAssets = state.assets.map((p) => {
            if (p.id === assets_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_ASSETS",
            payload: updatedAssets,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_ASSETS_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.assets, deleteDocumentById, ud_id]
  );

  const handleAssetsFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_ASSETS_FILES_TO_UPLOAD",
        payload: [...state.assetsFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.assetsFilestoUpload]
  );

  const handleAssetsFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.assetsFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_ASSETS_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.assetsFilestoUpload]
  );

  const handleAssetsFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_ASSETS_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchAssetsItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchAssetsData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "assets",
        });

        if (isMounted()) {
          dispatch({ type: "SET_ASSETS", payload: response });
        }
      } catch (error) {
        console.error("Error fetching assets:", error);
        // Set empty assets on error to prevent infinite retry loop
        if (isMounted()) {
          dispatch({ type: "SET_ASSETS", payload: [] });
        }
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchAssetsData, isMounted]
  );

  const handleAddOrUpdateAssetsSingleCall = useCallback(
    async (assetsItem: Asset): Promise<Asset | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(assetsItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateAssets(
            {
              ud_id: ud_id,
              entity: "assets",
              entity_id: assetsItem.id || undefined,
            },
            assetsItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createAssets(
            { ud_id: ud_id, entity: "assets" }, // Adjust according to your actual API parameter requirements
            assetsItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_ASSETS", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save assets item");
        console.error(error);
        return null;
      }
    },
    [ud_id, createAssets, updateAssets]
  );

  const handleAddOrUpdateAssets = useCallback(
    async (assetsItem: Asset) => {
      dispatch({ type: "SET_ASSETS_SAVING_NEW", payload: true });
      if (state.assetsFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateAssetsSingleCall(assetsItem);
          if (res) {
            await handleAssetsFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error("Failed to add or edit assets data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_ASSETS_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateAssetsSingleCall(assetsItem);
        } catch (error) {
          toast.error("Failed to add or edit assets data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_ASSETS_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateAssetsSingleCall,
      state.assetsFilestoUpload,
      handleAssetsFilesUpload,
    ]
  );

  const handleDeleteAssets = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_ASSETS_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteAssetsById({
          ud_id: ud_id,
          entity: "assets",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_ASSETS", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_ASSETS_SAVING_NEW", payload: false });
      }
    },
    [deleteAssetsById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchAssetsItems(ud_id); // Fetch all assets items
    }
  }, [ fetchAssetsItems, ud_id]);

  return {
    assets: state.assets,
    assetsLoading: state.assetsLoading,
    assetsSavingNew: state.assetsSavingNew,
    assetsFilestoUpload: state.assetsFilestoUpload,
    handleAddOrUpdateAssets,
    handleDeleteAssets,
    handleAssetsFileRemove,
    handleAssetsFilesDrop,
    handleAssetsFilesRemoveAll,
    handleDownloadAssetsDocument,
    handleDeleteAssetsDocument,
    handleAssetsFilesUpload,
  };
};
